<script lang="ts" setup>
import { colors } from '../../data/colors';
import { useLocaleStore } from '../../stores/locale';
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Swal from 'sweetalert2';
import axiosApiClient from '../../axios';
import { useUserStore } from '../../stores/user';
import { AxiosError } from 'axios';

const localeStore = useLocaleStore();
const userStore = useUserStore();
const isbusy = ref(false);
const t = useI18n().t;
interface LoginForm {
    email: string;
    password: string;
}
const form = ref<LoginForm>({
    email: '',
    password: '',
});
const errors = reactive({
    email: [],
    password: [],
});

const login = async (payload: LoginForm) => {
    // Reset errors
    errors.email = [];
    errors.password = [];
    isbusy.value = true;
    // Check if the form is valid
    if (!payload.email || !payload.password) {
        Swal.fire({
            icon: 'error',
            title: t('Oops...'),
            position: 'top-end',
            toast: true,
            text: t('Please fill in all fields!'),
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
        isbusy.value = false;
        return;
    }
    await axiosApiClient.get('/sanctum/csrf-cookie').then(async () => {
        // CSRF cookie has been set, you can now make authenticated requests
        try {
            const response = await axiosApiClient.post('/login', payload);
            // Handle successful login (e.g., redirect to dashboard)
            if (response.status !== 200) {
                Swal.fire({
                    icon: 'error',
                    title: t('Oops...'),
                    position: 'top-end',
                    toast: true,
                    text: response?.data?.message || 'Something went wrong!',
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true,
                });
                isbusy.value = false;
                return;
            }
            //get user
            const user_date = await axiosApiClient.get('/user');
            //set user in local storage
            const user_role = await userStore.getRole(user_date.data.user.id);
            user_date.data.user['ly'] = user_role.level;
            localStorage.setItem('user', JSON.stringify(user_date.data.user));
            //set token in local storage
            localStorage.setItem('token', user_date.data.token);
            //set token in local storage
            window.location.href = '/';
        } catch (e) {
            isbusy.value = false;
            if (e instanceof AxiosError) {
                if (e instanceof AxiosError && e.response?.status === 422) {
                    errors.email = e.response.data.errors.email || [];
                    errors.password = e.response.data.errors.password || [];
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('Oops...'),
                    text: 'Something went wrong!',
                    footer: '<a href="">Why do I have this issue?</a>',
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true,
                });
                return;
            }
        } 
    }).catch((error) => {
        console.error('Error setting CSRF cookie:', error);
        isbusy.value = false;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    });

};
const corp_info = ref({
    name: '',
    address: '',
    phone: '',
    mobile: '',
    email: '',
    description: '',
    logo: '',
});
onMounted(async () => {
    // Check if the user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
        // Redirect to the home page or dashboard
        window.location.href = '/';
    };
    // Fetch corporate information
    corp_info.value = localStorage.getItem('corp') ? JSON.parse(localStorage.getItem('corp') || '') : {
        name: '',
        address: '',
        phone: '',
        mobile: '',
        email: '',
        description: '',
        logo: '',
    };
});

</script>
<template>
    <app-layout>
        <div :style="{ backgroundColor: colors.secondaryDark }" class="login" :dir="localeStore.locale === 'ar' ? 'rtl' : 'ltr'">
            <form action="" @submit.prevent="login(form)">
                <!--image logo-->
                <!-- <div class="text-center mb-3">
                    <img :src="corp_info.logo ?? '../../assets/img/logo.png'" alt="logo" class="logo">
                </div> -->
                <h2>{{ t('Login') }}</h2>
                <div class="mb-3 mt-5">
                    <label for="email" class="form-label">{{ t('Email address Or Username') }}</label>
                    <input type="email" v-model="form.email" class="form-control" id="email"
                        aria-describedby="emailHelp">
                    <div v-if="errors.email.length > 0" class="text-danger mt-2">
                        <ul>
                            <li v-for="(error, index) in errors.email" :key="index">{{ error }}</li>
                        </ul>
                    </div>
                </div>
                <div class="mb-3">
                    <label for="password" class="form-label">{{ t('Password') }}</label>
                    <input type="password" v-model="form.password" class="form-control" id="password">
                    <div v-if="errors.password.length > 0" class="text-danger mt-2">
                        <ul>
                            <li v-for="(error, index) in errors.password" :key="index">{{ error }}</li>
                        </ul>
                    </div>
                </div>
                <div class="mt-3 text-center d-flex w-full gap-3 justify-content-around align-items-center">
                    <button type="submit" class="btn btn-primary">{{ t('Login') }}
                        <loading-spinner :is-loading="isbusy" />
                    </button>
                    <!--Home-->
                    <!-- <a href="/"
                        class="d-flex align-items-center gap-2 justify-content-center text-dark border border-secondary rounded px-2 home-btn"
                        style="padding-block: 2.2px ;">
                        <svg class="w-[32px] h-[32px] text-gray-800 dark:text-white" aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5" />
                        </svg>
                        {{ t('Home') }}</a> -->
                </div>
            </form>
            <div class="mt-3">
                <p>{{ t('Don\'t have an account ?') }} <a href="/register">{{ t('Register') }}</a></p>
                <p>{{ t('Forgot your password?') }}<a href="/forgot-password">{{ t('Reset Password') }}</a></p>
            </div>
        </div>
    </app-layout>
</template>



<style lang="scss" scoped>
.login-container {
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
}

.login {
    max-width: 400px;
    margin: 6vh auto;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    *{
        color: #fff !important;
    }
}

.logo {
    width: 150px;
    height: auto;
    margin-bottom: 20px;
}

h2 {
    margin-bottom: 20px;
    text-align: center;
    font-size: 24px;
    color: #333;
}

.home-btn {
    text-align: center;
    font-size: 16px;
    color: #333;
    text-decoration: none;
    border-radius: 4px;
    padding: 10px 20px;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.home-btn:hover {
    background-color: #845c3c;
    color: #ffffff;
}
</style>